window.addEventListener('load', async () => {
    /**
     * Documentation
     * Photoswipe: https://photoswipe.com/getting-started/
     * Add ui element: https://photoswipe.com/adding-ui-elements/ - used to add toggle full screen button in the toolbar
     * Add fullscreen: https://photoswipe.com/native-fullscreen-on-open/
     */

    try {

        await Promise.all([
            hoculus.utils.getCssAsync(window.hoculus.assets.photoswipe.photoswipeCss),
            hoculus.utils.getScriptAsync(window.hoculus.assets.photoswipe.photoswipeLightbox),
            hoculus.utils.getScriptAsync(window.hoculus.assets.photoswipe.photoswipe)
        ]);
    
        // Simple fullscreen API
        const fullscreenAPI = getFullscreenAPI();
    
        // Create custom container
        // which will be stretched to fullscreen.
        //
        // (we can not use PhotoSwipe root element (.pswp),
        //  as it is created only after openPromise is resolved)
        //
        const pswpContainer = getContainer();
    
        function getFullscreenPromise() {
            // Always resolve promise,
            // as wa want to open lightbox 
            // (no matter if fullscreen is supported or not)
            return new Promise((resolve) => {
                if (!fullscreenAPI || fullscreenAPI.isFullscreen()) {
                    // fullscreen API not supported, or already fullscreen
                    resolve();
                    return;
                }
    
                document.addEventListener(fullscreenAPI.change, (event) => {
                    // delay to make sure that browser fullscreen animation is finished
                    setTimeout(function () {
                        resolve();
                    }, 300);
                }, { once: true });
    
                fullscreenAPI.request(pswpContainer);
            });
        }
    
        const getOnOpenPromise = () => {
            return new Promise(resolve => {
                if (!fullscreenAPI || fullscreenAPI.isFullscreen()) {
                    resolve();
                    return;
                }
    
                pswpContainer.style.display = 'block';
                resolve()
            });
        }
    
        const lightbox = new PhotoSwipeLightbox({
            gallery: '.product__media-list',
            children: 'a',
            pswpModule: PhotoSwipe,
            mainClass: 'pswp--media-zoom',
    
            // Add function that returns promise
            openPromise: getOnOpenPromise,
    
            // Append PhotoSwipe to our container
            appendToEl: fullscreenAPI ? pswpContainer : document.body,
    
            // disable opening/closing animations
            showAnimationDuration: 0,
            hideAnimationDuration: 0,
    
            // Add if you're using responsive images
            // since viewport size is unpredictable
            // at initialization
            preloadFirstSlide: false
        });
    
        lightbox.on('close', () => {
            pswpContainer.style.display = 'none';
            if (fullscreenAPI && fullscreenAPI.isFullscreen()) {
                fullscreenAPI.exit();
            }
        });
    
        lightbox.on('uiRegister', function() {
            lightbox.pswp.ui.registerElement({
                name: 'toggle-fullscreen',
                ariaLabel: 'Toggle fullscreen',
                order: 10,
                html: `
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.--><path d="M128 96C110.3 96 96 110.3 96 128L96 224C96 241.7 110.3 256 128 256C145.7 256 160 241.7 160 224L160 160L224 160C241.7 160 256 145.7 256 128C256 110.3 241.7 96 224 96L128 96zM160 416C160 398.3 145.7 384 128 384C110.3 384 96 398.3 96 416L96 512C96 529.7 110.3 544 128 544L224 544C241.7 544 256 529.7 256 512C256 494.3 241.7 480 224 480L160 480L160 416zM416 96C398.3 96 384 110.3 384 128C384 145.7 398.3 160 416 160L480 160L480 224C480 241.7 494.3 256 512 256C529.7 256 544 241.7 544 224L544 128C544 110.3 529.7 96 512 96L416 96zM544 416C544 398.3 529.7 384 512 384C494.3 384 480 398.3 480 416L480 480L416 480C398.3 480 384 494.3 384 512C384 529.7 398.3 544 416 544L512 544C529.7 544 544 529.7 544 512L544 416z"/></svg>
                `,
                isButton: true,
                onClick: async (event, el) => {
                    if (fullscreenAPI && fullscreenAPI.isFullscreen()) {
                        fullscreenAPI.exit();
                        return;
                    }
    
                    getFullscreenPromise();
                }
            });
        });

        lightbox.on('verticalDrag', (e) => {
            const bg = lightbox.pswp.element.querySelector('.pswp__bg');

            // progress is from 0 → 1
            const progress = Math.abs(e.panY) / 300; // 300 = drag distance threshold
            const opacity = Math.max(0, 1 - progress);

            bg.style.setProperty('--bg-opacity', opacity);
        });

        lightbox.on('pointerUp', () => {
            const bg = lightbox.pswp.element.querySelector('.pswp__bg');
            bg.style.setProperty('--bg-opacity', 1);
        });

        lightbox.init();

        document.querySelector('.hoculus-product-media-gallery')?.classList?.add('photoswipe-loaded');
    
        // Simple fullscreen API helper,
        // supports unprefixed and webkit-prefixed versions
        function getFullscreenAPI() {
            let api;
            let enterFS;
            let exitFS;
            let elementFS;
            let changeEvent;
            let errorEvent;
    
            if (document.documentElement.requestFullscreen) {
                enterFS = 'requestFullscreen';
                exitFS = 'exitFullscreen';
                elementFS = 'fullscreenElement';
                changeEvent = 'fullscreenchange';
                errorEvent = 'fullscreenerror';
            } else if (document.documentElement.webkitRequestFullscreen) {
                enterFS = 'webkitRequestFullscreen';
                exitFS = 'webkitExitFullscreen';
                elementFS = 'webkitFullscreenElement';
                changeEvent = 'webkitfullscreenchange';
                errorEvent = 'webkitfullscreenerror';
            }
    
            if (enterFS) {
                api = {
                    request: function (el) {
                        if (enterFS === 'webkitRequestFullscreen') {
                            el[enterFS](Element.ALLOW_KEYBOARD_INPUT);
                        } else {
                            el[enterFS]();
                        }
                    },
    
                    exit: function () {
                        return document[exitFS]();
                    },
    
                    isFullscreen: function () {
                        return document[elementFS];
                    },
    
                    change: changeEvent,
                    error: errorEvent
                };
            }
    
            return api;
        };
    
        function getContainer() {
            const pswpContainer = document.createElement('div');
            pswpContainer.style.background = '#000';
            pswpContainer.style.width = '100%';
            pswpContainer.style.height = '100%';
            pswpContainer.style.display = 'none';
            document.body.appendChild(pswpContainer);
            return pswpContainer;
        }
        
    } catch (err) {
        console.log(err);
    }

});